import { useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Patient, Visit } from '../types';

export function usePatients() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchPatients = useCallback(async (query: string): Promise<Patient[]> => {
    if (!query.trim()) return [];

    setLoading(true);
    setError(null);

    try {
      const response = await api.patients.search(query);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search patients';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createPatient = useCallback(async (patient: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Promise<Patient | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patients.create(patient);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create patient';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPatientById = useCallback(async (patientId: string): Promise<Patient | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patients.getById(patientId);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch patient';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPatientHistory = useCallback(async (patientId: string): Promise<Visit[]> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patients.getVisits(patientId);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch patient history';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePatient = useCallback(async (patientId: string, updates: Partial<Patient>): Promise<Patient | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.patients.update(patientId, updates);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update patient';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    searchPatients,
    createPatient,
    getPatientById,
    getPatientHistory,
    updatePatient,
  };
}
